/**
 * Migration Utilities
 *
 * Functions to run data migrations from the browser console.
 * These use the authenticated user's session, so they respect Firestore security rules.
 *
 * HOW TO USE:
 * 1. Open the app in your browser and log in
 * 2. Open Developer Tools Console (F12)
 * 3. Run: window.migrations.addAddressHashToProperties()
 */

import { collection, getDocs, writeBatch, doc } from "firebase/firestore";
import { db } from "../firebase";
import { normalizeAddress, generatePropertyHash } from "./addressUtils";

/**
 * Add addressHash to all existing properties
 * Detects and reports duplicates
 */
export const addAddressHashToProperties = async () => {
  console.log("🚀 Starting addressHash migration...\n");

  try {
    // Fetch all properties
    console.log("📥 Fetching all properties...");
    const propertiesSnapshot = await getDocs(
      collection(db, "discovery_properties")
    );
    const properties = propertiesSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    console.log(`✅ Found ${properties.length} properties\n`);

    if (properties.length === 0) {
      console.log("ℹ️  No properties to update. Exiting.");
      return { success: true, updated: 0, duplicates: [] };
    }

    // Track duplicates found
    const hashMap = new Map();
    const duplicates = [];

    // Process properties and detect duplicates
    console.log("🔍 Generating address hashes and detecting duplicates...");
    properties.forEach((property) => {
      const hash = generatePropertyHash(property);

      if (hashMap.has(hash)) {
        // Duplicate found
        const existing = hashMap.get(hash);
        const existingDup = duplicates.find((d) => d.hash === hash);

        if (existingDup) {
          existingDup.properties.push(property);
        } else {
          duplicates.push({
            hash,
            properties: [existing, property],
          });
        }
      } else {
        hashMap.set(hash, property);
      }
    });

    console.log(`✅ Generated ${hashMap.size} unique hashes`);

    if (duplicates.length > 0) {
      console.log(`\n⚠️  Found ${duplicates.length} potential duplicate(s):\n`);
      duplicates.forEach((dup, index) => {
        console.log(
          `%c   Duplicate ${index + 1}:`,
          "font-weight: bold; color: #f59e0b"
        );
        dup.properties.forEach((prop) => {
          console.log(`     - ${prop.address} (${prop.postcode})`);
          console.log(`       ID: ${prop.id}`);
          console.log(
            `       Date Added: ${
              prop.dateAdded?.toDate
                ? prop.dateAdded.toDate().toLocaleDateString()
                : "Unknown"
            }`
          );
        });
        console.log("");
      });
    } else {
      console.log("✅ No duplicates found!\n");
    }

    // Update properties in batches (Firestore limit: 500 operations per batch)
    console.log("💾 Updating properties with addressHash...");
    const BATCH_SIZE = 500;
    let updatedCount = 0;

    for (let i = 0; i < properties.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      const batchProperties = properties.slice(i, i + BATCH_SIZE);

      batchProperties.forEach((property) => {
        const propertyRef = doc(db, "discovery_properties", property.id);
        const hash = generatePropertyHash(property);
        batch.update(propertyRef, { addressHash: hash });
      });

      await batch.commit();
      updatedCount += batchProperties.length;
      console.log(
        `   Updated ${updatedCount}/${properties.length} properties...`
      );
    }

    console.log(`\n✅ Migration complete! Updated ${updatedCount} properties.`);

    if (duplicates.length > 0) {
      console.log(`\n⚠️  WARNING: ${duplicates.length} duplicate(s) detected.`);
      console.log(
        "%cℹ️  Use the duplicate detection UI in the Properties tab to review and clean them up.",
        "color: #3b82f6"
      );
    }

    return {
      success: true,
      updated: updatedCount,
      duplicates: duplicates.map((dup) => ({
        hash: dup.hash,
        count: dup.properties.length,
        properties: dup.properties.map((p) => ({
          id: p.id,
          address: p.address,
          postcode: p.postcode,
          dateAdded: p.dateAdded?.toDate ? p.dateAdded.toDate() : null,
        })),
      })),
    };
  } catch (error) {
    console.error("\n❌ Error during migration:", error);

    if (error.code === "permission-denied") {
      console.error(
        "%c⚠️  Permission denied. Make sure you are logged in as an admin.",
        "color: #ef4444; font-weight: bold"
      );
    }

    return { success: false, error: error.message };
  }
};

// Make migrations available globally in the browser console
if (typeof window !== "undefined") {
  window.migrations = {
    addAddressHashToProperties,
  };
  console.log(
    "✅ Migration utilities loaded. Run: window.migrations.addAddressHashToProperties()"
  );
}
